import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// ── Global 401 interceptor ──
// Si el backend responde 401 (token expirado o inválido) en cualquier request,
// limpiamos el token local y redirigimos al login con un toast informativo.
// Esto evita el bug de "Token expirado" persistente en formularios largos.
let _interceptorAttached = false;
function setupAuthInterceptor() {
  if (_interceptorAttached) return;
  _interceptorAttached = true;
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || "";
      const isAuthExpired =
        status === 401 &&
        (detail === "Token expirado" || detail === "Token invalido" || detail === "No autenticado");
      if (isAuthExpired) {
        try {
          if (localStorage.getItem("token")) {
            localStorage.removeItem("token");
            // Aviso suave y redirección al login. Sólo si no estamos ya en /login.
            if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
              // Guardar la ruta actual para regresar después del re-login.
              try { sessionStorage.setItem("post_login_redirect", window.location.pathname + window.location.search); } catch {}
              // Pequeño delay para que el componente actual no pierda la animación.
              setTimeout(() => {
                window.location.href = "/login?expired=1";
              }, 300);
            }
          }
        } catch { /* ignore */ }
      }
      return Promise.reject(error);
    }
  );
}
setupAuthInterceptor();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) { setLoading(false); return; }
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(data);
    } catch {
      localStorage.removeItem("token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { /* ignore */ }
    localStorage.removeItem("token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
